import { isBlank, roundAmount } from './utils';
import { FormContext } from './useFormContext';

// eslint-disable-next-line max-lines-per-function
const useAmountDetails = (
  context: FormContext,
  vendorCurrency: string,
  formAmount: string
): [number, number, string, number, string] => {
  const { isDeposit, currency, fees, dynamicConversionFee, rate } = context;

  const formCurrency = isDeposit ? vendorCurrency : currency;

  const convertedAmount = (() => {
    if (isBlank(formAmount)) return 0;

    const roundedFormAmount = roundAmount(formAmount, formCurrency);
    return roundedFormAmount * rate;
  })();

  const amountWithoutFees = (() => {
    // fixed fee are applied differently for deposits and withdrawals.
    // Fixed fees are applied in vendor currency - so they are deducted
    // from the initial amount for deposits, but for withdrawals we
    // remove them from the final amount.
    const fixedFeeDeposit = isDeposit ? fees.fixed * rate : 0.0;
    const fixedFeeWithdrawal = isDeposit ? 0.0 : fees.fixed;

    const chargedConversion = (convertedAmount - fixedFeeDeposit) * (1 - dynamicConversionFee);
    const finalOperationFee = Math.max(chargedConversion * fees.operation, fees.min);

    return chargedConversion - finalOperationFee - fixedFeeWithdrawal;
  })();

  const amount = isDeposit ? roundAmount(amountWithoutFees, currency) : roundAmount(formAmount, currency);

  const resultSum = (() => {
    if (isBlank(formAmount)) return 0;

    const result = Math.max(amountWithoutFees, 0);
    return isDeposit ? roundAmount(result, currency) : roundAmount(result, vendorCurrency);
  })();

  const vendorAmount = isDeposit ? roundAmount(formAmount, vendorCurrency) : resultSum;

  const creditedCurrency = isDeposit ? currency : vendorCurrency;

  return [amount, vendorAmount, formCurrency, resultSum, creditedCurrency];
};

export default useAmountDetails;
